/**
 * Quiz funder-preview scorer.
 *
 * Simplified, deterministic port of the backend `funder_match.py`
 * likelihood engine — designed for the *pre-signup* Get-Started quiz
 * where we only have three signals from the founder:
 *   • stage           — "pre_revenue" | "post_revenue"
 *   • monthlyRevKES   — mid-point of the selected KES/month band (0 for pre-rev)
 *   • isProfitable    — true | false | null (only asked post-revenue)
 *
 * We convert monthly KES → annual USD at 140 KES/USD (the quoted rate in the
 * user brief) so we can reuse the annual-USD thresholds baked into the real
 * scoring rules. This is a *teaser* — the full paid analysis re-runs the
 * proper multi-factor scorer (sector, employees, docs, snapshot).
 */

const KES_PER_USD = 140;

// KES monthly-revenue bands the founder picks in Step 2.
// `midKES` = a defensible mid-point we score against internally.
export const REVENUE_BANDS = [
  { id: "lt_100k", label: "Less than KES 100,000 per month",         midKES:      50_000 },
  { id: "100k_1m", label: "KES 100,000 – 1,000,000 per month",       midKES:     500_000 },
  { id: "1m_5m",   label: "KES 1,000,000 – 5,000,000 per month",     midKES:  3_000_000 },
  { id: "5m_15m",  label: "KES 5,000,000 – 15,000,000 per month",    midKES: 10_000_000 },
  { id: "gt_15m",  label: "KES 15,000,000+ per month",               midKES:  20_000_000 },
];

// Copy for the funder-group cards. Order matters — matches the real report.
const FUNDER_META = {
  family_friends: {
    label: "Family & Friends",
    blurb: "Personal network — smallest cheques, fastest decisions.",
  },
  angels: {
    label: "Angel Investors",
    blurb: "High-net-worth individuals writing $5K-$250K cheques with mentorship.",
  },
  accelerators: {
    label: "Accelerators & Venture Studios",
    blurb: "Programmes that fund + build pre-revenue startups; ~$25K-$150K + mentorship.",
  },
  vcs: {
    label: "Venture Capital (VCs)",
    blurb: "Institutional funds chasing 10× returns. Tech, scalable, high-growth bias.",
  },
  pe: {
    label: "Private Equity (PEs)",
    blurb: "Institutional buyers of profitable, cash-generative businesses. $5M+ cheques.",
  },
  banks: {
    label: "Banks",
    blurb: "Debt facilities — need positive cash flow + collateral. No equity dilution.",
  },
  mfis: {
    label: "Microfinance Institutions",
    blurb: "Smaller working-capital loans, often unsecured. Higher rates than banks.",
  },
};

const LIKELIHOOD_RANK = { high: 3, medium: 2, low: 1 };

// Capital purposes asked in Step 4 (post-revenue businesses only).
// Multi-select with a primary tag — mirrors distinct funder pools.
export const CAPITAL_PURPOSES = [
  { id: "working_capital", label: "Working Capital",
    hint: "Day-to-day operating cash — payroll, rent, utilities, bridging the gap between paying suppliers and getting paid by customers. Usually short-term, revolving." },
  { id: "trade_finance", label: "Trade Finance",
    hint: "Financing tied to a specific trade transaction — importing goods, fulfilling a confirmed order, paying a supplier before the buyer pays you. Covers LCs, purchase-order financing, supplier credit." },
  { id: "asset_finance", label: "Asset Finance",
    hint: "Buying a specific asset — equipment, vehicles, machinery, property, solar/energy installations. Usually secured against the asset itself." },
  { id: "invoice_financing", label: "Invoice / Receivables Financing",
    hint: "Unlocking cash tied up in unpaid invoices — cash you've already earned but not yet collected, not a future purchase." },
  { id: "growth_capital", label: "Business Expansion / Growth Capital",
    hint: "Opening new locations, entering new markets, scaling headcount — typically equity or larger-ticket debt." },
  { id: "bridge_financing", label: "Bridge Financing",
    hint: "Short-term funding to cover a gap before a larger raise or expected cash event closes." },
  { id: "debt_refinancing", label: "Debt Refinancing / Restructuring",
    hint: "Replacing or consolidating existing debt on better terms." },
  { id: "other", label: "Something else",
    hint: "A capital need that doesn't fit the boxes above — tell us more and we'll route you to the right funder pool." },
];
export const CAPITAL_PURPOSE_LABEL = Object.fromEntries(
  CAPITAL_PURPOSES.map((p) => [p.id, p.label])
);

// Purpose-specific funder pools surfaced in the funder preview when the
// founder selects that capital purpose. Working capital and growth capital
// are already covered by the base groups (banks/MFIs and VCs/PE).
const PURPOSE_GROUPS = {
  trade_finance: {
    label: "Trade Finance Providers",
    blurb: "LCs, purchase-order financing and supplier credit tied to confirmed transactions.",
    rationale: "You selected trade finance — these funders underwrite the confirmed order or import itself, so a strong buyer or LC can carry the deal.",
  },
  asset_finance: {
    label: "Asset-Backed Lenders & Equipment Lessors",
    blurb: "Finance for equipment, vehicles, machinery, property and energy installations — secured on the asset.",
    rationale: "You selected asset finance — a distinct funder pool from working-capital lenders, since the asset itself is the collateral.",
  },
  invoice_financing: {
    label: "Invoice & Receivables Financiers",
    blurb: "Factoring and discounting houses that advance cash against unpaid invoices.",
    rationale: "You selected invoice financing — these funders buy or lend against receivables you've already earned.",
  },
  bridge_financing: {
    label: "Bridge & Short-Term Lenders",
    blurb: "Fast, short-tenor facilities that cover the gap to a larger raise or expected cash event.",
    rationale: "You selected bridge financing — speed and a clear repayment event matter more here than long trading history.",
  },
  debt_refinancing: {
    label: "Debt Restructuring Specialists",
    blurb: "Lenders and advisors who replace or consolidate existing facilities on better terms.",
    rationale: "You selected refinancing — these funders look at your existing debt stack and cash flows to reprice it.",
  },
};

/**
 * Score each funder group given the 3 quiz signals.
 * Returns an array of {id, label, blurb, likelihood, rationale} for the
 * 7 groups, sorted High → Medium → Low.
 *
 * @param {object} args
 * @param {string} args.stage           "pre_revenue" | "post_revenue"
 * @param {number} args.monthlyRevKES   0 for pre-rev, mid-point KES otherwise
 * @param {boolean|null} args.isProfitable  null for pre-rev
 */
export function scoreFunderPreview({ stage, monthlyRevKES, isProfitable, purposes = [] }) {
  const isPreRev = stage === "pre_revenue";
  const annualUSD = ((monthlyRevKES || 0) * 12) / KES_PER_USD;
  const profitable = isProfitable === true;

  // ────────── Per-group rules ──────────
  const rules = {
    family_friends: () => {
      if (isPreRev || annualUSD < 10_000)
        return ["high", "Pre-revenue / very-early stage — friends & family are typically the first cheque."];
      if (annualUSD < 50_000)
        return ["medium", "Early-stage businesses still lean on personal network for top-ups."];
      return ["low", "Once you have material revenue, professional funders are a faster fit."];
    },

    angels: () => {
      if (isPreRev || annualUSD < 250_000)
        return ["high", "Angels lean into seed-stage stories with a clear thesis and early traction."];
      if (annualUSD < 1_000_000)
        return ["medium", "Some angels syndicate into Series A — your readiness score will matter."];
      return ["low", "Most angels exit by Series A; VCs / PE take over from here."];
    },

    accelerators: () => {
      if (isPreRev)
        return ["high", "Pre-revenue stage — accelerators and venture studios are designed exactly for this moment."];
      if (annualUSD < 200_000)
        return ["high", "Seed-stage founders are still in scope for accelerator cohorts and studio investments."];
      if (annualUSD < 500_000)
        return ["medium", "Some accelerators run later-stage cohorts; expect a higher bar than at seed."];
      return ["low", "You're past the typical accelerator window — VCs / banks are a faster fit."];
    },

    vcs: () => {
      // Quiz doesn't ask for sector — so we can't confirm "tech / scalable".
      // Treat VC as *possible* rather than *high* by default; profitability
      // isn't a VC signal (they favour growth), so it doesn't shift the rank.
      if (isPreRev)
        return ["medium", "Pre-seed VCs will consider strong founding teams — sector fit will drive the final decision."];
      if (annualUSD >= 500_000)
        return ["high", "Revenue at this level puts you in scope for growth-stage VCs — sector + growth rate will decide."];
      if (annualUSD >= 100_000)
        return ["medium", "Seed-stage VCs are interested; readiness score + sector will drive the final call."];
      return ["low", "VCs typically need a scalable model + real traction — focus on angels / accelerators first."];
    },

    pe: () => {
      // PE cares hard about profitability + size.
      if (annualUSD >= 1_000_000 && profitable)
        return ["high", "$1M+ revenue and profitable — PE sweet spot for cash-generative businesses."];
      if (annualUSD >= 500_000 && profitable)
        return ["medium", "Approaching PE territory — grow revenue while keeping margins healthy."];
      if (annualUSD >= 1_000_000)
        return ["medium", "Revenue is there, but PE funds want proven profitability — improve margins first."];
      return ["low", "PE funds rarely look below $1M revenue with real profit — come back when you cross that line."];
    },

    banks: () => {
      // Debt lenders care most about profitability + steady cash flow.
      if (!profitable)
        return ["low", "Banks need positive cash flow before extending credit — profitability comes first."];
      if (annualUSD >= 500_000)
        return ["high", "Profitable with $500K+ revenue — banks lend against this all day. Debt beats dilution."];
      if (annualUSD >= 200_000)
        return ["high", "Profitable at this revenue band — banks will consider a working-capital facility."];
      if (annualUSD >= 50_000)
        return ["medium", "Profitable but small — banks may lend against collateral; expect stricter terms."];
      return ["medium", "Profitable businesses can access small business loans; larger facilities need scale."];
    },

    mfis: () => {
      // MFIs specifically serve small profitable SMEs.
      if (profitable && annualUSD > 0 && annualUSD < 200_000)
        return ["high", "MFIs specifically serve profitable SMEs in your revenue band — fast-turnaround loans."];
      if (profitable && annualUSD < 500_000)
        return ["medium", "MFIs may approve small working-capital loans; rates higher than banks."];
      if (isPreRev)
        return ["low", "MFIs typically want a revenue history before lending — return once you're trading."];
      if (annualUSD >= 500_000)
        return ["low", "You've outgrown most MFI ceilings — banks will give you better terms."];
      return ["medium", "MFIs sometimes lend against strong personal guarantees even without formal profit — worth exploring."];
    },
  };

  const purposeRows = (purposes || [])
    .filter((p) => PURPOSE_GROUPS[p])
    .map((p) => ({ id: p, ...PURPOSE_GROUPS[p], likelihood: "high" }));

  const rows = [
    ...purposeRows,
    ...Object.entries(FUNDER_META).map(([id, meta]) => {
      const [likelihood, rationale] = rules[id]();
      return { id, ...meta, likelihood, rationale };
    }),
  ];

  // Sort High → Medium → Low. Tie-break by original order (Object.entries
  // returns insertion order, so this is stable).
  rows.sort((a, b) => LIKELIHOOD_RANK[b.likelihood] - LIKELIHOOD_RANK[a.likelihood]);
  return rows;
}

/**
 * Take the sorted preview and return the top N groups that are High or
 * Medium fit — capped at 4. If the founder is a total edge case (all
 * "low"), we still return the top 3 so the page isn't empty.
 */
export function topFitGroups(rows, cap = 4) {
  const good = rows.filter((r) => r.likelihood !== "low");
  if (good.length >= 2) return good.slice(0, cap);
  return rows.slice(0, 3);
}
